import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, MessageSquare, Briefcase, Settings, LogOut,
  Users, Mail, Eye, Trash2, X, CheckCircle, Clock, AlertCircle,
  Plus, Search, Filter, ChevronDown, BarChart3, TrendingUp, TrendingDown, CreditCard, UserCircle, KeyRound, Layers
} from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ServicesAdmin from '../components/ServicesAdmin';
import PortfolioAdmin from '../components/PortfolioAdmin';
import ClientsAdmin from '../components/ClientsAdmin';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [contacts, setContacts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [stats, setStats] = useState({ totalContacts: 0, newContacts: 0, totalProjects: 0, featuredProjects: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [settings, setSettings] = useState({
    companyName: '',
    email: '',
    phone: '',
    address: '',
    logo: '',
    workingDays: '',
    workingHours: ''
  });
  const [settingsMessage, setSettingsMessage] = useState('');
  const [pricingPlans, setPricingPlans] = useState([]);
  const [pricingMessage, setPricingMessage] = useState('');
  const [adminProfile, setAdminProfile] = useState({ name: '', email: '' });
  const [accountForm, setAccountForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [accountMessage, setAccountMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [portfolioFeaturedOnly, setPortfolioFeaturedOnly] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contactsRes, portfolioRes] = await Promise.all([
        api.get('/contact'),
        api.get('/portfolio')
      ]);
      setContacts(contactsRes.data);
      setPortfolio(portfolioRes.data);
      setStats({
        totalContacts: contactsRes.data.length,
        newContacts: contactsRes.data.filter(c => c.status === 'new').length,
        totalProjects: portfolioRes.data.length,
        featuredProjects: portfolioRes.data.filter(p => p.featured).length
      });
    } catch (err) {
      // Demo data fallback
      setContacts([
        { _id: '1', name: 'أحمد محمد', email: 'ahmed@test.com', phone: '0500000001', subject: 'استفسار', message: 'أريد نظام ERP', status: 'new', createdAt: '2026-08-06' },
        { _id: '2', name: 'خالد عبدالله', email: 'khaled@test.com', phone: '0500000002', subject: 'طلب عرض سعر', message: 'نظام محاسبي', status: 'read', createdAt: '2026-08-05' },
        { _id: '3', name: 'فاطمة الزهراني', email: 'fatima@test.com', phone: '0500000003', subject: 'دعم فني', message: 'مشكلة في النظام', status: 'replied', createdAt: '2026-08-04' },
      ]);
      setPortfolio([
        { _id: '1', title: 'نظام المستشفيات', category: 'medical', featured: true },
        { _id: '2', title: 'نظام العقارات', category: 'realestate', featured: true },
        { _id: '3', title: 'نظام المحاسبة', category: 'accounting', featured: false },
      ]);
      setStats({ totalContacts: 3, newContacts: 1, totalProjects: 3, featuredProjects: 2 });
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('orvix_token');
    localStorage.removeItem('orvix_user');
    toast.success('تم تسجيل الخروج');
    navigate('/admin/login');
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الرسالة؟')) return;
    try {
      await api.delete(`/contact/${id}`);
      setContacts(contacts.filter(c => c._id !== id));
      toast.success('تم الحذف بنجاح');
    } catch (err) {
      toast.error('خطأ في الحذف');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.put(`/contact/${id}/status`, { status });
      setContacts(contacts.map(c => c._id === id ? { ...c, status } : c));
      toast.success('تم تحديث الحالة');
    } catch (err) {
      toast.error('خطأ في التحديث');
    }
  };

  const filteredContacts = contacts.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });


  useEffect(() => {
    if (activeTab === 'settings') fetchSettings();
    if (activeTab === 'account') fetchAdminProfile();
    if (activeTab === 'pricing') fetchPricingPlans();
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get('/settings');
      setSettings({
        companyName: String(data.companyName || ''),
        email: String(data.email || ''),
        phone: String(data.phone || ''),
        address: String(data.address || ''),
        logo: typeof data.logo === 'string' ? data.logo : '',
        workingDays: String(data.workingDays || ''),
        workingHours: String(data.workingHours || '')
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };


  const handleLogoChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSettingsMessage('يرجى اختيار ملف صورة');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setSettingsMessage('حجم الصورة يجب ألا يتجاوز 2 ميجابايت');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSettings(prev => ({ ...prev, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/settings', settings);
      setSettingsMessage('تم حفظ الإعدادات بنجاح');
      setTimeout(() => setSettingsMessage(''), 3000);
    } catch (error) {
      setSettingsMessage('حدث خطأ أثناء حفظ الإعدادات');
    }
  };

  const fetchAdminProfile = async () => {
    try {
      const { data } = await api.get('/auth/profile');
      setAdminProfile({ name: data.name || '', email: data.email || '' });
    } catch (error) {
      setAccountMessage('تعذر تحميل بيانات حساب الإدارة');
    }
  };

  const handleSaveAdminProfile = async () => {
    if (accountForm.newPassword && accountForm.newPassword !== accountForm.confirmPassword) {
      setAccountMessage('تأكيد كلمة المرور الجديدة غير مطابق');
      return;
    }
    try {
      const payload = {
        name: adminProfile.name,
        email: adminProfile.email,
        currentPassword: accountForm.currentPassword,
      };
      if (accountForm.newPassword) payload.newPassword = accountForm.newPassword;
      const { data } = await api.put('/auth/profile', payload);
      localStorage.setItem('orvix_token', data.token);
      localStorage.setItem('orvix_user', JSON.stringify(data));
      setAdminProfile({ name: data.name || '', email: data.email || '' });
      setAccountForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setAccountMessage('تم تحديث حساب الإدارة بنجاح');
      setTimeout(() => setAccountMessage(''), 3500);
    } catch (error) {
      setAccountMessage(error.response?.data?.message || 'حدث خطأ أثناء تحديث حساب الإدارة');
    }
  };

  const fetchPricingPlans = async () => {
    try {
      const { data } = await api.get('/pricing');
      setPricingPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      setPricingMessage('تعذر تحميل الباقات');
    }
  };


  const handleAddPricingPlan = async () => {
    try {
      const nextOrder = pricingPlans.length
        ? Math.max(...pricingPlans.map(p => Number(p.order || 0))) + 1
        : 1;

      const { data } = await api.post('/pricing', {
        name: `Package ${pricingPlans.length + 1}`,
        nameAr: `باقة جديدة ${pricingPlans.length + 1}`,
        price: '',
        priceZatca: '',
        period: '',
        desc: '',
        popular: false,
        order: nextOrder,
        features: [],
        disabled: []
      });

      setPricingPlans(prev => [...prev, data]);
      setPricingMessage('تمت إضافة باقة جديدة. يمكنك تعديل اسمها وبياناتها ثم الضغط على حفظ الباقة.');
      setTimeout(() => setPricingMessage(''), 4000);
    } catch (error) {
      setPricingMessage(error.response?.data?.message || 'حدث خطأ أثناء إضافة الباقة');
    }
  };

  const updatePricingPlanField = (id, field, value) => {
    setPricingPlans(prev => prev.map(plan => plan._id === id ? { ...plan, [field]: value } : plan));
  };

  const handleSavePricingPlan = async (plan) => {
    try {
      const payload = {
        name: plan.name,
        nameAr: plan.nameAr,
        price: plan.price,
        priceZatca: plan.priceZatca,
        period: plan.period,
        desc: plan.desc,
        popular: Boolean(plan.popular),
        order: plan.order,
        features: Array.isArray(plan.features) ? plan.features : [],
        disabled: Array.isArray(plan.disabled) ? plan.disabled : []
      };
      const { data } = await api.put(`/pricing/${plan._id}`, payload);
      setPricingPlans(prev => prev.map(p => p._id === plan._id ? data : p));
      setPricingMessage(`تم حفظ باقة ${data.name} بنجاح`);
      setTimeout(() => setPricingMessage(''), 3000);
    } catch (error) {
      setPricingMessage('حدث خطأ أثناء حفظ الباقة');
    }
  };

  const statusColors = {
    new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    read: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    replied: 'bg-green-500/20 text-green-400 border-green-500/30',
    archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };

  const statusLabels = { new: 'جديد', read: 'مقروء', replied: 'تم الرد', archived: 'مؤرشف' };

  const sidebarItems = [
    { id: 'dashboard', label: 'لوحة المعلومات', icon: LayoutDashboard },
    { id: 'contacts', label: 'رسائل التواصل', icon: MessageSquare },
    { id: 'portfolio', label: 'المشاريع', icon: Briefcase },
    { id: 'clients', label: 'إدارة شركاء النجاح', icon: Users },
    { id: 'services', label: 'الخدمات', icon: Layers },
    { id: 'pricing', label: 'الباقات', icon: CreditCard },
    { id: 'settings', label: 'الإعدادات', icon: Settings },
    { id: 'account', label: 'حساب الإدارة', icon: UserCircle },
  ];

  return (
    <div className="min-h-screen bg-orvix-dark flex">
      {/* Sidebar */}
      <aside className="w-64 bg-orvix-navy border-l border-white/10 flex-shrink-0 hidden lg:flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orvix-primary to-orvix-green rounded-xl flex items-center justify-center text-white font-bold overflow-hidden">{settings.logo ? <img src={settings.logo} alt="ORVIX" className="w-full h-full object-cover" /> : 'O'}</div>
            <span className="text-lg font-bold text-white">{adminProfile.name || 'ORVIX Admin'}</span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === item.id
                  ? 'bg-orvix-primary/20 text-orvix-primary'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all">
            <LogOut size={18} />
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-orvix-navy border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <span className="text-white font-bold">ORVIX Admin</span>
        <button onClick={handleLogout} className="text-red-400 text-sm">خروج</button>
      </div>

      {/* Main Content */}
      <main className="flex-1 p-8 lg:p-10 pt-20 lg:pt-10 overflow-auto">
        {activeTab === 'dashboard' && (
          <div>
            <h1 className="text-2xl font-bold text-white mb-8">لوحة المعلومات</h1>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              <button type="button" onClick={() => { setStatusFilter('all'); setActiveTab('contacts'); }} className="glass p-6 text-right hover:bg-white/10 transition-all cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <MessageSquare className="w-8 h-8 text-orvix-primary" />
                  <span className="text-xs text-green-400 flex items-center gap-1"><TrendingUp size={12} /> +12%</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stats.totalContacts}</div>
                <div className="text-gray-400 text-sm">إجمالي الرسائل</div>
              </button>
              <button type="button" onClick={() => { setStatusFilter('new'); setActiveTab('contacts'); }} className="glass p-6 text-right hover:bg-white/10 transition-all cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <Mail className="w-8 h-8 text-orvix-green" />
                  <span className="text-xs text-green-400 flex items-center gap-1"><TrendingUp size={12} /> +5</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stats.newContacts}</div>
                <div className="text-gray-400 text-sm">رسائل جديدة</div>
              </button>
              <button type="button" onClick={() => { setPortfolioFeaturedOnly(false); setActiveTab('portfolio'); }} className="glass p-6 text-right hover:bg-white/10 transition-all cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <Briefcase className="w-8 h-8 text-amber-500" />
                  <span className="text-xs text-gray-400 flex items-center gap-1"><TrendingDown size={12} /> 0%</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stats.totalProjects}</div>
                <div className="text-gray-400 text-sm">المشاريع</div>
              </button>
              <button type="button" onClick={() => { setPortfolioFeaturedOnly(true); setActiveTab('portfolio'); }} className="glass p-6 text-right hover:bg-white/10 transition-all cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <BarChart3 className="w-8 h-8 text-violet-500" />
                  <span className="text-xs text-green-400 flex items-center gap-1"><TrendingUp size={12} /> +2</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stats.featuredProjects}</div>
                <div className="text-gray-400 text-sm">مشاريع مميزة</div>
              </button>
            </div>

            <div className="glass p-6">
              <h3 className="text-lg font-bold text-white mb-6">آخر الرسائل</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-right text-gray-400 text-sm font-medium pb-3">الاسم</th>
                      <th className="text-right text-gray-400 text-sm font-medium pb-3">الموضوع</th>
                      <th className="text-right text-gray-400 text-sm font-medium pb-3">الحالة</th>
                      <th className="text-right text-gray-400 text-sm font-medium pb-3">التاريخ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.slice(0, 5).map((contact) => (
                      <tr key={contact._id} onClick={() => setSelectedContact(contact)} className="border-b border-white/5 hover:bg-white/10 transition-colors cursor-pointer" title="اضغط لعرض تفاصيل الرسالة">
                        <td className="py-3 text-white text-sm">{contact.name}</td>
                        <td className="py-3 text-gray-400 text-sm">{contact.subject || '—'}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs border ${statusColors[contact.status]}`}>
                            {statusLabels[contact.status]}
                          </span>
                        </td>
                        <td className="py-3 text-gray-500 text-sm">{new Date(contact.createdAt).toLocaleDateString('ar-SA')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <h1 className="text-2xl font-bold text-white">رسائل التواصل</h1>
              <div className="flex gap-3">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="بحث..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pr-10 pl-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:border-orvix-green focus:outline-none w-48"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-orvix-green focus:outline-none"
                >
                  <option value="all" className="bg-orvix-navy">الكل</option>
                  <option value="new" className="bg-orvix-navy">جديد</option>
                  <option value="read" className="bg-orvix-navy">مقروء</option>
                  <option value="replied" className="bg-orvix-navy">تم الرد</option>
                  <option value="archived" className="bg-orvix-navy">مؤرشف</option>
                </select>
              </div>
            </div>

            <div className="glass overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="text-right text-gray-400 text-xs font-medium py-3 px-4">الاسم</th>
                      <th className="text-right text-gray-400 text-xs font-medium py-3 px-4">البريد</th>
                      <th className="text-right text-gray-400 text-xs font-medium py-3 px-4">الموضوع</th>
                      <th className="text-right text-gray-400 text-xs font-medium py-3 px-4">الحالة</th>
                      <th className="text-right text-gray-400 text-xs font-medium py-3 px-4">التاريخ</th>
                      <th className="text-right text-gray-400 text-xs font-medium py-3 px-4">إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map((contact) => (
                      <tr key={contact._id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-3 px-4 text-white text-sm">{contact.name}</td>
                        <td className="py-3 px-4 text-gray-400 text-sm">{contact.email}</td>
                        <td className="py-3 px-4 text-gray-400 text-sm">{contact.subject || '—'}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs border ${statusColors[contact.status]}`}>
                            {statusLabels[contact.status]}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-sm">{new Date(contact.createdAt).toLocaleDateString('ar-SA')}</td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <button onClick={() => setSelectedContact(contact)} className="p-1.5 rounded-lg bg-orvix-primary/20 text-orvix-primary hover:bg-orvix-primary/30 transition-colors">
                              <Eye size={14} />
                            </button>
                            <button onClick={() => handleDeleteContact(contact._id)} className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredContacts.length === 0 && (
                <div className="text-center py-12 text-gray-500">لا توجد رسائل</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'portfolio' && (
          <PortfolioAdmin
            featuredOnly={portfolioFeaturedOnly}
            onChanged={fetchData}
          />
        )}

        {activeTab === 'clients' && <ClientsAdmin />}

        {activeTab === 'services' && <ServicesAdmin />}

        {activeTab === 'pricing' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-white">إدارة الباقات</h1>
                <p className="text-gray-500 text-sm mt-1">يمكنك إضافة باقات جديدة وتعديل الأسماء والمميزات، والسعر اختياري. أي تعديل محفوظ يظهر في الموقع الخارجي.</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleAddPricingPlan} className="btn-primary text-sm">+ إضافة باقة جديدة</button>
                <button onClick={fetchPricingPlans} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 text-sm">تحديث</button>
              </div>
            </div>

            {pricingMessage && <div className="mb-5 px-4 py-3 rounded-xl bg-orvix-green/10 border border-orvix-green/20 text-orvix-green text-sm">{pricingMessage}</div>}

            <div className="space-y-6">
              {pricingPlans.map((plan, index) => (
                <div key={plan._id || index} className="glass p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-white">{plan.name || `باقة ${index + 1}`}</h3>
                      <p className="text-gray-500 text-sm">{plan.nameAr}</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input type="checkbox" checked={Boolean(plan.popular)} onChange={(e) => updatePricingPlanField(plan._id, 'popular', e.target.checked)} className="w-4 h-4" />
                      الأكثر طلباً
                    </label>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div><label className="block text-gray-300 text-sm mb-2">اسم الباقة بالإنجليزية</label><input value={plan.name || ''} onChange={(e) => updatePricingPlanField(plan._id, 'name', e.target.value)} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" /></div>
                    <div><label className="block text-gray-300 text-sm mb-2">اسم الباقة بالعربية</label><input value={plan.nameAr || ''} onChange={(e) => updatePricingPlanField(plan._id, 'nameAr', e.target.value)} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" /></div>
                    <div><label className="block text-gray-300 text-sm mb-2">السعر (اختياري)</label><input value={plan.price || ''} onChange={(e) => updatePricingPlanField(plan._id, 'price', e.target.value)} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" /></div>
                    <div><label className="block text-gray-300 text-sm mb-2">السعر مع ربط الزكاة (اختياري)</label><input value={plan.priceZatca || ''} onChange={(e) => updatePricingPlanField(plan._id, 'priceZatca', e.target.value)} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" /></div>
                    <div><label className="block text-gray-300 text-sm mb-2">الفترة (اختياري)</label><input value={plan.period || ''} onChange={(e) => updatePricingPlanField(plan._id, 'period', e.target.value)} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" /></div>
                    <div><label className="block text-gray-300 text-sm mb-2">الوصف</label><input value={plan.desc || ''} onChange={(e) => updatePricingPlanField(plan._id, 'desc', e.target.value)} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" /></div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div><label className="block text-gray-300 text-sm mb-2">المميزات — ميزة في كل سطر</label><textarea rows="8" value={(plan.features || []).join('\n')} onChange={(e) => updatePricingPlanField(plan._id, 'features', e.target.value.split('\n'))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none resize-y" /></div>
                    <div><label className="block text-gray-300 text-sm mb-2">المميزات غير المتاحة — عنصر في كل سطر</label><textarea rows="8" value={(plan.disabled || []).join('\n')} onChange={(e) => updatePricingPlanField(plan._id, 'disabled', e.target.value.split('\n'))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none resize-y" /></div>
                  </div>

                  <div className="mt-5 flex justify-end"><button onClick={() => handleSavePricingPlan(plan)} className="btn-primary">حفظ الباقة</button></div>
                </div>
              ))}

              {pricingPlans.length === 0 && <div className="glass p-10 text-center text-gray-500">لا توجد باقات للعرض</div>}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <h1 className="text-2xl font-bold text-white mb-8">الإعدادات</h1>
            <div className="space-y-6 max-w-2xl">
            <div className="glass p-8">
              <h3 className="text-lg font-bold text-white mb-6">معلومات الموقع</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">اسم الشركة</label>
                  <input type="text" value={settings.companyName} onChange={(e) => setSettings({...settings, companyName: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">البريد الإلكتروني</label>
                  <input type="email" value={settings.email} onChange={(e) => setSettings({...settings, email: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">رقم الهاتف</label>
                  <input type="tel" value={settings.phone} onChange={(e) => setSettings({...settings, phone: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">العنوان</label>
                  <input type="text" value={settings.address} onChange={(e) => setSettings({...settings, address: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">أيام العمل</label>
                    <input type="text" value={settings.workingDays} onChange={(e) => setSettings({...settings, workingDays: e.target.value})} placeholder="مثال: الأحد - الخميس" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">ساعات العمل</label>
                    <input type="text" value={settings.workingHours} onChange={(e) => setSettings({...settings, workingHours: e.target.value})} placeholder="مثال: 9:00 ص - 6:00 م" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">شعار الشركة</label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-xl bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center text-white font-bold text-2xl">
                      {settings.logo ? <img src={settings.logo} alt="معاينة الشعار" className="w-full h-full object-cover" /> : 'O'}
                    </div>
                    <div className="flex-1">
                      <input type="file" accept="image/*" onChange={handleLogoChange} className="block w-full text-sm text-gray-300 file:ml-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orvix-primary/20 file:text-orvix-primary hover:file:bg-orvix-primary/30" />
                      <p className="text-xs text-gray-500 mt-2">PNG أو JPG، بحد أقصى 2 ميجابايت</p>
                      {settings.logo && <button type="button" onClick={() => setSettings({...settings, logo: ''})} className="text-xs text-red-400 mt-2">إزالة الصورة</button>}
                    </div>
                  </div>
                </div>
                <button onClick={handleSaveSettings} className="btn-primary mt-4">حفظ التغييرات</button>
                {settingsMessage && <p className="mt-3 text-sm text-green-400">{settingsMessage}</p>}
              </div>
            </div>


            </div>
          </div>
        )}


        {activeTab === 'account' && (
          <div>
            <h1 className="text-2xl font-bold text-white mb-8">حساب الإدارة</h1>
            <div className="space-y-6 max-w-2xl">
            <div className="glass p-8">
              <div className="flex items-center gap-3 mb-6">
                <UserCircle className="text-orvix-green" size={22} />
                <div>
                  <h3 className="text-lg font-bold text-white">حساب الإدارة</h3>
                  <p className="text-xs text-gray-500 mt-1">هذا البريد هو المستخدم لتسجيل الدخول واسترجاع كلمة المرور.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">اسم المسؤول</label>
                  <input type="text" value={adminProfile.name} onChange={(e) => setAdminProfile({...adminProfile, name: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">بريد تسجيل الدخول والاسترجاع</label>
                  <input type="email" value={adminProfile.email} onChange={(e) => setAdminProfile({...adminProfile, email: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" />
                </div>
                <div className="pt-3 border-t border-white/10">
                  <div className="flex items-center gap-2 text-white font-medium mb-4"><KeyRound size={18} /> تغيير كلمة المرور</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-gray-300 text-sm mb-2">كلمة المرور الحالية</label>
                      <input type="password" value={accountForm.currentPassword} onChange={(e) => setAccountForm({...accountForm, currentPassword: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" autoComplete="current-password" />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">كلمة المرور الجديدة</label>
                      <input type="password" value={accountForm.newPassword} onChange={(e) => setAccountForm({...accountForm, newPassword: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" autoComplete="new-password" placeholder="8 أحرف على الأقل" />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">تأكيد كلمة المرور الجديدة</label>
                      <input type="password" value={accountForm.confirmPassword} onChange={(e) => setAccountForm({...accountForm, confirmPassword: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-orvix-green focus:outline-none" autoComplete="new-password" />
                    </div>
                  </div>
                </div>
                <button onClick={handleSaveAdminProfile} className="btn-primary">حفظ بيانات حساب الإدارة</button>
                {accountMessage && <p className="text-sm text-orvix-green">{accountMessage}</p>}
              </div>
            </div>
            </div>
          </div>
        )}
      </main>

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedContact(null)}>
          <div className="glass max-w-lg w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelectedContact(null)} className="absolute top-4 left-4 text-gray-400 hover:text-white">
              <X size={24} />
            </button>
            <h3 className="text-xl font-bold text-white mb-6">تفاصيل الرسالة</h3>
            <div className="space-y-4">
              <div>
                <span className="text-gray-500 text-sm">الاسم:</span>
                <p className="text-white">{selectedContact.name}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">البريد:</span>
                <p className="text-white">{selectedContact.email}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">الهاتف:</span>
                <p className="text-white">{selectedContact.phone || '—'}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">الموضوع:</span>
                <p className="text-white">{selectedContact.subject || '—'}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">الرسالة:</span>
                <p className="text-gray-300 mt-1 p-4 rounded-xl bg-white/5">{selectedContact.message}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">الحالة:</span>
                <div className="flex gap-2 mt-2">
                  {Object.keys(statusLabels).map((status) => (
                    <button
                      key={status}
                      onClick={() => handleUpdateStatus(selectedContact._id, status)}
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                        selectedContact.status === status
                          ? statusColors[status]
                          : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                      }`}
                    >
                      {statusLabels[status]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
